import { fullLabel } from "../components/WhereItIs";
import { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import api, { formatApiErrorDetail } from "@/lib/api";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { useAuth } from "@/context/AuthContext";
import { YardSectorMove } from "@/components/YardSectorMove";
import { RackLevel } from "@/components/places/RackLevel";
import { PrintLabelsLauncher } from "@/components/places/PrintLabelsLauncher";
import { statusLabel } from "@/lib/status";
import { toast } from "sonner";
import { MapPin, Package, Boxes, PackagePlus, MoveRight, Pencil, Check, X, Loader2, Clock, Printer } from "lucide-react";

// Compact relative time ("3d ago") for the stale-bin last-activity stamp.
function timeAgo(iso) {
  if (!iso) return null;
  const then = new Date(iso).getTime();
  if (isNaN(then)) return null;
  const s = Math.max(0, Math.floor((Date.now() - then) / 1000));
  if (s < 60) return "just now";
  const m = Math.floor(s / 60); if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60); if (h < 24) return `${h}h ago`;
  const d = Math.floor(h / 24); if (d < 30) return `${d}d ago`;
  const mo = Math.floor(d / 30); if (mo < 12) return `${mo}mo ago`;
  return `${Math.floor(mo / 12)}y ago`;
}

// Scan-a-location / scan-a-container HUB: what's here + description + the right move actions.
// Fixed bin/shelf/spot -> "Put items here" only (can't relocate). Movable container -> add parts +
// "Move to a bin/shelf" (scan/pick) + "Move to a yard sector" (dropdown, camera-free).
export default function ScanLocationPage() {
  const { token } = useParams();
  const navigate = useNavigate();
  const { has } = useAuth();
  const [loc, setLoc] = useState(null);
  const [contents, setContents] = useState(null);
  const [error, setError] = useState("");
  const [editingDesc, setEditingDesc] = useState(false);
  const [descDraft, setDescDraft] = useState("");
  const [savingDesc, setSavingDesc] = useState(false);

  const load = async () => {
    try {
      const { data } = await api.get(`/scan/loc/${token}`);
      setLoc(data);
      setDescDraft(data.description || "");
      const c = await api.get(`/locations/${data.id}/contents`);
      setContents(c.data);
    } catch (e) { setError(formatApiErrorDetail(e.response?.data?.detail)); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [token]);

  const isContainer = loc?.type === "container";
  const canMove = has("putaway");

  const saveDesc = async () => {
    setSavingDesc(true);
    try {
      await api.put(`/locations/${loc.id}`, { description: descDraft });
      setLoc({ ...loc, description: descDraft });
      setEditingDesc(false);
      toast.success("Description saved");
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setSavingDesc(false); }
  };

  if (error) return (
    <div className="max-w-lg mx-auto" data-testid="scan-location-page">
      <Link to="/fixed-locations" className="font-mono text-xs text-[#B01E28] underline">← locations</Link>
      <div className="border border-destructive text-destructive p-4 text-sm mt-3" data-testid="scan-error">{error}</div>
    </div>
  );
  if (!loc) return (
    <div className="max-w-lg mx-auto flex items-center gap-2 text-slate-500 text-sm py-10" data-testid="scan-location-loading">
      <Loader2 className="w-4 h-4 animate-spin" /> Opening label…
    </div>
  );

  return (
    <div className="max-w-lg mx-auto" data-testid="scan-location-page">
      <Link to="/fixed-locations" className="font-mono text-xs text-[#B01E28] underline">← locations</Link>

      {/* Header */}
      <div className="border border-slate-200 bg-white rounded-xl p-5 mt-3" data-testid="scan-loc-header">
        <div className="flex items-center gap-2">
          {isContainer ? <Boxes className="w-5 h-5 text-[#B01E28]" /> : <MapPin className="w-5 h-5 text-[#B01E28]" />}
          <h1 className="font-display text-xl font-bold">{loc.name}</h1>
          <Badge className="rounded-lg bg-slate-900 text-white text-[10px] uppercase ml-auto" data-testid="scan-loc-type">
            {isContainer ? (loc.container_type || "container") : loc.type}
          </Badge>
        </div>
        <div className="font-mono text-2xl font-black tracking-tight mt-3" data-testid="scan-loc-code">{loc.short_code}</div>
        <RackLevel level={loc.rack_level} testid="scan-loc-level" />
        <div className="font-mono text-xs text-slate-500 mt-1.5 flex items-center gap-2 flex-wrap" data-testid="scan-loc-path">
          {fullLabel(loc)}
          {loc.is_lot_crate && <Badge variant="outline" className="rounded text-[10px]">Lot crate</Badge>}
          {loc.holding && <Badge variant="outline" className="rounded text-[10px] border-amber-400 text-amber-700">Holding</Badge>}
          {loc.label_printed_at
            ? <span className="text-emerald-600">· label printed</span>
            : <span className="text-slate-400">· label not printed</span>}
        </div>
        {loc.address && <div className="text-sm text-slate-600 mt-2">{loc.address}</div>}

        {/* Description (optional; editable with manage_locations) */}
        <div className="mt-3 pt-3 border-t border-slate-100" data-testid="scan-loc-description">
          {editingDesc ? (
            <div className="space-y-2">
              <Textarea data-testid="scan-loc-desc-input" value={descDraft} onChange={(e) => setDescDraft(e.target.value)}
                placeholder="e.g. heavy engine cores, north wall" className="rounded-lg text-sm" rows={2} />
              <div className="flex gap-2">
                <Button data-testid="scan-loc-desc-save" size="sm" onClick={saveDesc} disabled={savingDesc} className="rounded-lg h-8">
                  {savingDesc ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <><Check className="w-3.5 h-3.5 mr-1" />Save</>}
                </Button>
                <Button data-testid="scan-loc-desc-cancel" size="sm" variant="outline"
                  onClick={() => { setEditingDesc(false); setDescDraft(loc.description || ""); }}
                  className="rounded-lg h-8"><X className="w-3.5 h-3.5 mr-1" />Cancel</Button>
              </div>
            </div>
          ) : (
            <div className="flex items-start gap-2">
              <p className={`text-sm flex-1 ${loc.description ? "text-slate-700" : "text-slate-400 italic"}`} data-testid="scan-loc-desc-text">
                {loc.description || "No description"}
              </p>
              {has("manage_locations") && (
                <Button data-testid="scan-loc-desc-edit" size="sm" variant="ghost" onClick={() => setEditingDesc(true)}
                  className="h-7 px-2 text-slate-500"><Pencil className="w-3.5 h-3.5" /></Button>
              )}
            </div>
          )}
        </div>
      </div>

      <PrintLabelsLauncher locId={loc.id} button={({ onClick, label }) => (
        <Button data-testid="scan-loc-print-labels" onClick={onClick}
          className="w-full mt-3 justify-start rounded-lg bg-[#1F1F1F] hover:bg-black text-white h-11">
          <Printer className="w-4 h-4 mr-2" />{label}
        </Button>)} />

      {/* Actions */}
      {canMove && (
        <div className="border border-slate-200 bg-white rounded-xl p-4 mt-3 space-y-2" data-testid="scan-loc-actions">
          <div className="text-[11px] font-bold uppercase tracking-wider text-slate-500">Actions</div>
          <Button data-testid="scan-loc-put-here" onClick={() => navigate(`/putaway?dest=${encodeURIComponent(loc.short_code)}`)}
            className="w-full justify-start rounded-lg bg-[#1F1F1F] hover:bg-black text-white h-11">
            <PackagePlus className="w-4 h-4 mr-2" />{isContainer ? "Add parts to this container" : "Put items here"}
          </Button>
          {isContainer && (<>
            <Button data-testid="scan-loc-move-bin" variant="outline"
              onClick={() => navigate(`/move-tote?tote=${encodeURIComponent(loc.short_code)}`)}
              className="w-full justify-start rounded-lg border-slate-300 h-11">
              <MoveRight className="w-4 h-4 mr-2" />Move to a bin / shelf (scan or pick)
            </Button>
            <div className="pt-1">
              <div className="text-[11px] text-slate-500 mb-1">Move to a yard sector (fast, camera-free)</div>
              <YardSectorMove kind="container" containerScan={loc.short_code} onDone={load} testid="scan-loc-yard" />
            </div>
          </>)}
        </div>
      )}

      {/* Contents */}
      <div className="border border-slate-200 bg-white rounded-xl p-4 mt-3" data-testid="scan-loc-contents">
        <div className="flex items-center gap-2 text-[11px] font-bold uppercase tracking-wider text-slate-500 mb-2">
          <Package className="w-3.5 h-3.5" />What's here
          {contents && (
            <span className="ml-auto font-mono normal-case text-slate-400">
              {contents.part_count} part{contents.part_count === 1 ? "" : "s"} · {contents.container_count} container{contents.container_count === 1 ? "" : "s"}
            </span>
          )}
        </div>
        {contents && (
          <div className="flex items-center gap-1.5 text-[11px] font-mono text-slate-400 mb-2 -mt-1" data-testid="scan-loc-last-activity">
            <Clock className="w-3 h-3" />
            {contents.last_activity ? `Last activity ${timeAgo(contents.last_activity)}` : "No recorded activity"}
          </div>
        )}
        {!contents ? (
          <div className="text-sm text-slate-400 py-4 text-center font-mono flex items-center justify-center gap-2">
            <Loader2 className="w-4 h-4 animate-spin" />Loading…
          </div>
        ) : (contents.part_count === 0 && contents.container_count === 0) ? (
          <div className="text-sm text-slate-400 py-4 text-center font-mono" data-testid="scan-loc-empty">EMPTY</div>
        ) : (
          <div className="divide-y divide-slate-100" data-testid="scan-loc-contents-list">
            {contents.containers.map((c) => (
              <div key={c.id} className="flex items-center gap-2 py-2 text-sm" data-testid={`scan-loc-container-${c.short_code}`}>
                <Boxes className="w-4 h-4 text-[#C76714] shrink-0" />
                <span className="font-mono text-xs font-bold">{c.short_code}</span>
                <span className="text-slate-600 truncate">{c.name}</span>
                {c.is_lot_crate && <Badge variant="outline" className="rounded text-[10px] ml-auto">Lot</Badge>}
              </div>
            ))}
            {contents.parts.map((p) => (
              <div key={p.id} onClick={() => navigate(`/parts/${p.id}`)} data-testid={`scan-loc-part-${p.short_code}`}
                className="flex items-center gap-2 py-2 text-sm cursor-pointer hover:bg-slate-50 -mx-1 px-1 rounded">
                <Package className="w-4 h-4 text-[#B01E28] shrink-0" />
                <span className="font-mono text-xs font-bold">{p.short_code}</span>
                <span className="flex-1 text-slate-700 truncate">{p.name}</span>
                <Badge variant="outline" className="rounded text-[10px] shrink-0">{statusLabel(p.status)}</Badge>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
